import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Check, Send, Vote } from 'lucide-react';
import { Button } from '../ui/Button';
import type { PropsCaptura } from './registro';
import { CampoDeclarativo } from './CapturaTexto';

/**
 * Captura de la primitiva `voto` (Bolsillo, 380px, targets ≥44px).
 *
 * Con `captura.presupuesto` el voto se siente como FICHAS QUE SE GASTAN:
 * una fila de círculos (llenos = gastados, huecos = disponibles), el conteo
 * «Te quedan X de N votos» y, al agotarse, opciones deshabilitadas con un
 * mensaje humano. El backend valida igual el presupuesto (D6): esta UI es
 * cortesía, no la barrera real. Sin presupuesto: lista simple de opciones.
 *
 * Oleada 3: si el manifiesto declara campos EXTRA además de `opcion` (p. ej.
 * el porqué obligatorio del rumble), el voto deja de ser un toque: se elige
 * la opción, se llenan los campos y se envía con botón — la decisión viaja
 * con su justificación en UN aporte citable.
 */
export function CapturaVoto({ captura, deshabilitado, enviando, onEnviar, misAportes }: PropsCaptura) {
  const presupuesto = captura.presupuesto;
  const [elegida, setElegida] = useState<string | null>(null);
  const [valores, setValores] = useState<Record<string, string | number>>({});
  // Campos extra del payload_schema (todo lo que no es la opción del voto).
  const extras = Object.keys(captura.campos).filter((n) => n !== 'opcion');
  const conExtras = extras.length > 0;

  // Mis votos ya enviados en esta actividad, contados por opción del payload.
  const misVotos = (misAportes ?? []).filter((a) => a.tipo === 'voto');
  const votosPorOpcion: Record<string, number> = {};
  for (const voto of misVotos) {
    const opcion = voto.payload.opcion;
    if (typeof opcion === 'string') {
      votosPorOpcion[opcion] = (votosPorOpcion[opcion] ?? 0) + 1;
    }
  }

  const gastados = presupuesto === null ? 0 : Math.min(misVotos.length, presupuesto);
  const restantes = presupuesto === null ? null : presupuesto - gastados;
  const agotado = restantes !== null && restantes <= 0;

  return (
    <div className="flex flex-col gap-3">
      {presupuesto !== null && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-sunken px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5" aria-hidden="true">
            {Array.from({ length: presupuesto }, (_, i) => (
              <span
                key={i}
                className={`h-4 w-4 rounded-full transition-colors duration-fast ${
                  i < gastados ? 'bg-primary' : 'border-2 border-border-strong bg-surface-raised'
                }`}
              />
            ))}
          </div>
          <p className="font-sans text-sm text-content-muted">
            {agotado
              ? `Repartiste tus ${presupuesto} votos. Como el presupuesto: cuando se acaba, se acaba.`
              : `Te quedan ${restantes} de ${presupuesto} votos`}
          </p>
        </div>
      )}

      {captura.opcionesTotal !== null && captura.opcionesTotal > captura.opciones.length && (
        <p className="font-sans text-xs text-content-muted">
          Mostrando las primeras {captura.opciones.length} de {captura.opcionesTotal} opciones.
        </p>
      )}

      {captura.opciones.map((op) => {
        const veces = votosPorOpcion[op.valor] ?? 0;
        const activa = conExtras && elegida === op.valor;
        return (
          <button
            key={op.valor}
            type="button"
            role={conExtras ? 'radio' : undefined}
            aria-checked={conExtras ? activa : undefined}
            disabled={enviando || deshabilitado || agotado}
            onClick={() =>
              conExtras ? setElegida(op.valor) : onEnviar({ opcion: op.valor })
            }
            aria-label={
              veces > 0
                ? `${op.etiqueta} (ya le diste ${veces === 1 ? 'un voto' : `${veces} votos`})`
                : op.etiqueta
            }
            className={`flex min-h-touch items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left font-sans text-base font-medium text-content transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
              activa
                ? 'border-primary bg-surface-sunken'
                : veces > 0
                  ? 'border-primary bg-surface-raised'
                  : 'border-border-strong bg-surface-raised'
            }`}
          >
            <span className="min-w-0 flex-1">{op.etiqueta}</span>
            {veces > 0 ? (
              <span className="flex shrink-0 items-center gap-1 text-primary">
                <Check className="h-5 w-5" aria-hidden="true" />
                {veces > 1 && <span className="font-display text-sm font-semibold">×{veces}</span>}
              </span>
            ) : (
              <Vote className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            )}
          </button>
        );
      })}

      {conExtras && !agotado && (
        <VotoConCampos
          extras={extras}
          captura={captura}
          elegida={elegida}
          valores={valores}
          setValores={setValores}
          deshabilitado={deshabilitado}
          enviando={enviando}
          onEnviar={onEnviar}
        />
      )}
    </div>
  );
}

/** Campos extra + botón de envío del voto con justificación (Oleada 3). */
function VotoConCampos({
  extras,
  captura,
  elegida,
  valores,
  setValores,
  deshabilitado,
  enviando,
  onEnviar,
}: {
  extras: string[];
  captura: PropsCaptura['captura'];
  elegida: string | null;
  valores: Record<string, string | number>;
  setValores: Dispatch<SetStateAction<Record<string, string | number>>>;
  deshabilitado: boolean;
  enviando: boolean;
  onEnviar: PropsCaptura['onEnviar'];
}) {
  const faltantes: string[] = [];
  if (elegida === null) faltantes.push('tu opción');
  for (const nombre of extras) {
    const campo = captura.campos[nombre];
    if (campo.requerido === false) continue;
    const v = valores[nombre];
    const lleno = typeof v === 'number' || (typeof v === 'string' && v.trim().length > 0);
    if (!lleno) {
      faltantes.push(
        campo.etiqueta ?? nombre.charAt(0).toUpperCase() + nombre.slice(1),
      );
    }
  }
  const completo = faltantes.length === 0;

  const enviar = () => {
    const payload: Record<string, unknown> = { opcion: elegida };
    for (const nombre of extras) {
      const v = valores[nombre];
      if (v !== undefined && v !== '') payload[nombre] = v;
    }
    onEnviar(payload);
  };

  return (
    <>
      {extras.map((nombre) => (
        <CampoDeclarativo
          key={nombre}
          nombre={nombre}
          campo={captura.campos[nombre]}
          valor={valores[nombre]}
          onCambiar={(v) =>
            setValores((prev) => {
              if (v === undefined) {
                const { [nombre]: _omitido, ...resto } = prev;
                return resto;
              }
              return { ...prev, [nombre]: v };
            })
          }
          deshabilitado={enviando || deshabilitado}
        />
      ))}
      <Button
        variant="primary"
        fullWidth
        loading={enviando}
        disabled={!completo || deshabilitado}
        onClick={enviar}
        leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
      >
        Enviar mi voto
      </Button>
      {!completo && (
        <p className="font-sans text-sm text-content-muted" aria-live="polite">
          Falta: {faltantes.join(', ')}
        </p>
      )}
    </>
  );
}
